// src/pages/PlayerStatsPage.jsx
// Everybody who has played in any of the user's tournaments, with their results added up.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { subscribeToAuth } from '../auth';
import { apiRequest } from '../api';
import AppShell from '../components/AppShell';
import { ArrowLeftIcon } from '../components/icons';
import { formatDay } from '../utils/time';

const SORTS = {
    games: { label: 'Games played', compare: (a, b) => b.games - a.games },
    won: { label: 'Matches won', compare: (a, b) => b.matchesWon - a.matchesWon },
    rate: { label: 'Win rate', compare: (a, b) => (b.winRate ?? -1) - (a.winRate ?? -1) },
    tournaments: { label: 'Tournaments', compare: (a, b) => b.tournamentCount - a.tournamentCount },
    titles: { label: 'Titles and podiums', compare: (a, b) => b.titles - a.titles || b.podiums - a.podiums },
    name: { label: 'Name (A–Z)', compare: (a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName) },
};

const percent = (rate) => (rate === null ? '–' : `${Math.round(rate * 100)}%`);
const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

function Person({ person }) {
    const [open, setOpen] = useState(false);
    const name = `${person.firstName} ${person.lastName}`;
    return (
        <li className="person">
            <div className="person-head">
                <button className="section-toggle" aria-expanded={open} aria-label={`${name}, results per tournament`} onClick={() => setOpen((prev) => !prev)}>
                    <strong>{name}</strong>
                </button>
                {person.titles > 0 && <span className="badge badge-primary">{plural(person.titles, 'title')}</span>}
                {person.podiums > 0 && <span className="badge">{plural(person.podiums, 'podium')}</span>}
            </div>
            <div className="person-stats">
                <span>{plural(person.tournamentCount, 'tournament')}</span>
                <span>{plural(person.matches, 'match', 'matches')}: {person.matchesWon}–{person.matchesLost}{person.matchesDrawn ? ` (${person.matchesDrawn})` : ''}</span>
                <span title="Matches won of matches played">Win rate {percent(person.winRate)}</span>
                <span>Sets {person.setsWon}–{person.setsLost}</span>
                <span>Points {person.pointsFor}–{person.pointsAgainst}</span>
                {person.bestRank !== null && <span>Best place {person.bestRank}</span>}
            </div>
            {open && (
                <table className="person-tournaments">
                    <thead>
                        <tr>
                            <th scope="col">Tournament</th>
                            <th scope="col">Place</th>
                            <th scope="col">Matches</th>
                            <th scope="col">Sets</th>
                        </tr>
                    </thead>
                    <tbody>
                        {person.tournaments.map((t) => (
                            <tr key={t.tournamentId}>
                                <th scope="row">
                                    <Link to={`/tournament/${t.tournamentId}`}>{t.name}</Link>
                                    {t.date && <span className="muted"> · {formatDay(t.date)}</span>}
                                </th>
                                <td>{t.rank === null ? '–' : `${t.rank} of ${t.of}`}</td>
                                <td>{t.matchesWon}–{t.matchesLost}{t.matchesDrawn ? ` (${t.matchesDrawn})` : ''}</td>
                                <td>{t.setsWon}–{t.setsLost}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </li>
    );
}

function PlayerStatsPage() {
    const user = useContext(UserContext);
    const navigate = useNavigate();
    const [people, setPeople] = useState(null);
    const [failed, setFailed] = useState(false);
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState('games');

    useEffect(() => {
        const unsubscribe = subscribeToAuth((authUser) => {
            if (!authUser) navigate('/');
        });
        return () => unsubscribe?.();
    }, [navigate]);

    useEffect(() => {
        if (!user?.uid) return;
        apiRequest('GET', '/playerStats', user.uid)
            .then(setPeople)
            .catch((error) => {
                console.error('Error loading the player statistics:', error);
                setFailed(true);
            });
    }, [user]);

    const shown = useMemo(() => {
        if (!people) return [];
        const words = query.toLowerCase().split(/\s+/).filter(Boolean);
        const matching = people.filter((p) => words.every((word) => `${p.firstName} ${p.lastName}`.toLowerCase().includes(word)));
        return [...matching].sort((a, b) => SORTS[sort].compare(a, b) || SORTS.name.compare(a, b));
    }, [people, query, sort]);

    return (
        <AppShell user={user}>
            <div className="page-head">
                <div className="titles">
                    <button className="btn btn-ghost btn-sm back-link" onClick={() => navigate('/dashboard')}>
                        <ArrowLeftIcon size={16} /> Back to dashboard
                    </button>
                    <h1>Player statistics</h1>
                    <p className="muted">
                        Results added up over all your tournaments. People are matched by name (capitals and extra spaces are
                        ignored), so two different people with the same name count as one.
                    </p>
                </div>
            </div>

            {failed ? (
                <div className="empty">
                    <strong>The statistics could not be loaded</strong>
                    <p>Please try again in a moment.</p>
                </div>
            ) : people === null ? (
                <div className="skeleton" aria-busy="true" />
            ) : people.length === 0 ? (
                <div className="empty">
                    <strong>No players yet</strong>
                    <p>Add players to a tournament and play some rounds.</p>
                </div>
            ) : (
                <section className="card stats-card" aria-label="Players">
                    <div className="player-tools">
                        <input className="input player-search" type="search" aria-label="Search players" placeholder="Search players" value={query} onChange={(event) => setQuery(event.target.value)} />
                        <select className="input player-sort" aria-label="Sort players" value={sort} onChange={(event) => setSort(event.target.value)}>
                            {Object.entries(SORTS).map(([key, { label }]) => (
                                <option key={key} value={key}>{label}</option>
                            ))}
                        </select>
                    </div>
                    {shown.length === 0 ? (
                        <p className="hint">No players match "{query.trim()}".</p>
                    ) : (
                        <ul className="people">
                            {shown.map((person) => (
                                <Person key={`${person.firstName}|${person.lastName}`} person={person} />
                            ))}
                        </ul>
                    )}
                </section>
            )}
        </AppShell>
    );
}

export default PlayerStatsPage;
